import { fmtUnit } from '@/lib/format-unit'
import type { PricingItem } from '@/types'

/* Formula-linked items are now identified by PricingItem.formulaKey field
 * rather than a hardcoded name set. No sync needed between frontend/backend. */

/* ── X-in-circle delete icon ── */
const XCircleIcon = () => (
  <svg style={{ width: 20, height: 20 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M15 9l-6 6" />
    <path d="M9 9l6 6" />
  </svg>
)

/* ── Link icon for formula-linked items ── */
const LinkIcon = () => (
  <svg style={{ width: 18, height: 18 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
)

/* Format a number as dollars — returns '—' for 0/undefined when `dashOnZero` is true */
const fmt = (v: number | undefined, dashOnZero = false): string => {
  if (v === undefined || v === null) return '—'
  if (dashOnZero && v === 0) return '—'
  return `$${v.toFixed(2)}`
}

/* Unit display — delegates to shared fmtUnit formatter */
const unitLabel = fmtUnit

interface PricingItemsTableProps {
  items: PricingItem[]
  category: string
  onEdit: (item: PricingItem) => void
  onDelete: (id: string) => void
  searchQuery: string
  onItemUpdated?: (updated: PricingItem) => void
}

export function PricingItemsTable({
  items,
  category,
  onEdit,
  onDelete,
  searchQuery,
}: PricingItemsTableProps) {
  const filtered = items
    .filter(
      (item) =>
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.service?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.stripingCategory?.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => a.name.localeCompare(b.name))

  if (filtered.length === 0) {
    return (
      <div className="est-list-empty">
        <svg className="est-list-empty-icon" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
          <path d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
        </svg>
        <p className="est-list-empty-text">
          {items.length === 0 ? 'No pricing items yet' : 'No items match your search'}
        </p>
        <p className="est-list-empty-sub">
          {items.length === 0
            ? 'Add your first pricing item to get started'
            : 'Try a different search term'}
        </p>
      </div>
    )
  }

  const rowStyle = { cursor: 'pointer' } as const
  const costStyle = { fontWeight: 600, color: 'var(--s900)' } as const

  /* Stop the delete click from also triggering the row's onEdit */
  const handleDelete = (e: React.MouseEvent, id: string) => {
    e.stopPropagation()
    onDelete(id)
  }

  /* Helper: get service names from serviceRates or comma-separated service field */
  const getServiceNames = (item: PricingItem): string[] => {
    if (item.serviceRates && typeof item.serviceRates === 'object') {
      return Object.keys(item.serviceRates)
    }
    return item.service ? item.service.split(',').map(s => s.trim()).filter(Boolean) : []
  }

  /* ── Equipment: Name, Services (pills), Delete ── */
  if (category === 'equipment') {
    return (
      <table className="tbl">
        <thead>
          <tr>
            <th>Name</th>
            <th>Services</th>
            <th style={{ width: 48 }} />
          </tr>
        </thead>
        <tbody>
          {filtered.map((item) => {
            const svcNames = getServiceNames(item)
            return (
              <tr key={item.id} style={rowStyle} onClick={() => onEdit(item)}>
                <td style={{ fontWeight: 500, color: 'var(--s900)' }}>{item.name}</td>
                <td>
                  {svcNames.length > 0
                    ? <span className="prc-svc-chips">
                        {svcNames.map(s => <span key={s} className="prc-svc-chip-tag">{s}</span>)}
                      </span>
                    : '—'}
                </td>
                <td style={{ textAlign: 'right' }}>
                  <button className="tbl-action-btn tbl-action-btn--danger" title="Delete" onClick={(e) => handleDelete(e, item.id)}>
                    <XCircleIcon />
                  </button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    )
  }

  /* ── Labor: Name, Services (pills), Delete ── */
  if (category === 'labor') {
    return (
      <table className="tbl">
        <thead>
          <tr>
            <th>Name</th>
            <th>Services</th>
            <th style={{ width: 48 }} />
          </tr>
        </thead>
        <tbody>
          {filtered.map((item) => {
            const svcNames = getServiceNames(item)
            return (
              <tr key={item.id} style={rowStyle} onClick={() => onEdit(item)}>
                <td style={{ fontWeight: 500, color: 'var(--s900)' }}>{item.name}</td>
                <td>
                  {svcNames.length > 0
                    ? <span className="prc-svc-chips">
                        {svcNames.map(s => <span key={s} className="prc-svc-chip-tag">{s}</span>)}
                      </span>
                    : '—'}
                </td>
                <td style={{ textAlign: 'right' }}>
                  <button className="tbl-action-btn tbl-action-btn--danger" title="Delete" onClick={(e) => handleDelete(e, item.id)}>
                    <XCircleIcon />
                  </button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    )
  }

  /* ── Miscellaneous: Name, Services (pills), Delete ── */
  if (category === 'miscellaneous') {
    return (
      <table className="tbl">
        <thead>
          <tr>
            <th>Name</th>
            <th>Services</th>
            <th style={{ width: 48 }} />
          </tr>
        </thead>
        <tbody>
          {filtered.map((item) => {
            const svcNames = getServiceNames(item)
            return (
              <tr key={item.id} style={rowStyle} onClick={() => onEdit(item)}>
                <td style={{ fontWeight: 500, color: 'var(--s900)' }}>{item.name}</td>
                <td>
                  {svcNames.length > 0
                    ? <span className="prc-svc-chips">
                        {svcNames.map(s => <span key={s} className="prc-svc-chip-tag">{s}</span>)}
                      </span>
                    : '—'}
                </td>
                <td style={{ textAlign: 'right' }}>
                  <button className="tbl-action-btn tbl-action-btn--danger" title="Delete" onClick={(e) => handleDelete(e, item.id)}>
                    <XCircleIcon />
                  </button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    )
  }

  /* ── Materials: Name, Services (pills), Unit Type ── */
  if (category === 'materials') {
    return (
      <table className="tbl">
        <thead>
          <tr>
            <th>Name</th>
            <th>Services</th>
            <th>Unit Type</th>
            <th style={{ width: 48 }} />
          </tr>
        </thead>
        <tbody>
          {filtered.map((item) => {
            const svcNames = getServiceNames(item)
            const isProtected = !!item.formulaKey
            return (
              <tr key={item.id} style={rowStyle} onClick={() => onEdit(item)}>
                <td style={{ fontWeight: 500, color: 'var(--s900)' }}>
                  {item.name}
                </td>
                <td>
                  {svcNames.length > 0
                    ? <span className="prc-svc-chips">
                        {svcNames.map(s => <span key={s} className="prc-svc-chip-tag">{s}</span>)}
                      </span>
                    : fmt(item.rate, true)}
                </td>
                <td>{unitLabel(item.unit)}</td>
                <td style={{ textAlign: 'right' }}>
                  {isProtected ? (
                    <span className="prc-linked-icon" title="Linked to writeup formulas — cannot be deleted">
                      <LinkIcon />
                    </span>
                  ) : (
                    <button className="tbl-action-btn tbl-action-btn--danger" title="Delete" onClick={(e) => handleDelete(e, item.id)}>
                      <XCircleIcon />
                    </button>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    )
  }

  /* ── Striping: Worktype, Restripe Cost, New Layout Cost (if enabled), Double Coat Cost (if enabled), Category ── */
  if (category === 'striping') {
    return (
      <table className="tbl">
        <thead>
          <tr>
            <th>Worktype</th>
            <th>Restripe Cost</th>
            <th>New Layout Cost</th>
            <th>Double Coat Cost</th>
            <th>Category</th>
            <th style={{ width: 48 }} />
          </tr>
        </thead>
        <tbody>
          {filtered.map((item) => (
            <tr key={item.id} style={rowStyle} onClick={() => onEdit(item)}>
              <td style={{ fontWeight: 500, color: 'var(--s900)' }}>{item.name}</td>
              <td style={costStyle}>{fmt(item.restripeCost ?? item.rate)}</td>
              <td>{item.newLayoutEnabled ? fmt(item.newLayoutAdditionalCost) : '—'}</td>
              <td>{item.doubleCoatEnabled ? fmt(item.doubleCoatAdditionalCost) : '—'}</td>
              <td>{item.stripingCategory || '—'}</td>
              <td style={{ textAlign: 'right' }}>
                <button className="tbl-action-btn tbl-action-btn--danger" title="Delete" onClick={(e) => handleDelete(e, item.id)}>
                  <XCircleIcon />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    )
  }

  /* Default fallback: Name, Description, Unit, Rate */
  return (
    <table className="tbl">
      <thead>
        <tr>
          <th>Item Name</th>
          <th>Description</th>
          <th>Unit</th>
          <th>Rate</th>
          <th style={{ width: 48 }} />
        </tr>
      </thead>
      <tbody>
        {filtered.map((item) => (
          <tr key={item.id} style={rowStyle} onClick={() => onEdit(item)}>
            <td style={{ fontWeight: 500, color: 'var(--s900)' }}>{item.name}</td>
            <td style={{ color: 'var(--s500)', fontSize: 13 }}>{item.description || '—'}</td>
            <td>{item.unit}</td>
            <td style={costStyle}>{fmt(item.rate)}</td>
            <td style={{ textAlign: 'right' }}>
              <button className="tbl-action-btn tbl-action-btn--danger" title="Delete" onClick={(e) => handleDelete(e, item.id)}>
                <XCircleIcon />
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
